import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Spinner, Alert } from 'react-bootstrap';
import axios from 'axios';
import { ApiUrl, Headers } from '../services/ApiUrl';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const ResultadosVotacion = () => {
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

    useEffect(() => {
        const fetchResultados = async () => {
            try {
                const url = ApiUrl + 'getresultados';
                const response = await axios.get(url, { headers: Headers });
                
                if (response.data.error) {
                    throw new Error(response.data.mensaje);
                }
                
                setData(response.data);
                setError(null);
            } catch (err) {
                setError('Error al cargar los resultados: ' + err.message);
                console.error('Error:', err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchResultados();
    }, []);

    if (isLoading) {
        return (
            <Container className="mt-4 text-center">
                <Spinner animation="border" variant="primary" />
                <p>Cargando resultados...</p>
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="mt-4">
                <Alert variant="danger">{error}</Alert>
            </Container>
        );
    }

    const dataBarras = [
        { name: 'Trenque Lauquen', votos: data.totales['PP General']['Trenque Lauquen'] },
        { name: 'Beruti', votos: data.totales['PP General']['Beruti'] },
        { name: '30 de Agosto', votos: data.totales['PP General']['30 de Agosto'] },
        { name: 'PP Joven', votos: data.totales['PP Joven'] }
    ];

    const dataPie = [
        { name: 'Votos Online', value: data.estadisticas.votos_online },
        { name: 'Votos Presenciales', value: data.estadisticas.votos_presenciales }
    ];

    const isWinner = (index, category) => {
        switch (category) {
            case 'Trenque Lauquen':
                return index < 3;
            case '30 de Agosto':
            case 'Beruti':
            case 'PP Joven':
                return index === 0;
            default:
                return false;
        }
    };

    const winnerStyle = {
        fontWeight: 'bold',
        backgroundColor: 'rgba(40, 167, 69, 0.1)'
    };

    const renderResultTable = (proyectos, category) => (
        <Table responsive striped hover>
            <thead>
                <tr>
                    <th>#</th>
                    <th>Proyecto</th>
                    <th className="text-center">Votos</th>
                    <th className="text-center">Porcentaje</th>
                </tr>
            </thead>
            <tbody>
                {proyectos.map((proyecto, index) => {
                    const total = category === 'PP Joven' 
                        ? data.totales['PP Joven']
                        : data.totales['PP General'][category];
                    const percentage = ((proyecto.votos / total) * 100).toFixed(1);

                    return (
                        <tr 
                            key={proyecto.id}
                            style={isWinner(index, category) ? winnerStyle : {}}
                        >
                            <td>{proyecto.id}</td>
                            <td>{proyecto.titulo}</td>
                            <td className="text-center">{proyecto.votos}</td>
                            <td className="text-center">{percentage}%</td>
                        </tr>
                    );
                })}
            </tbody>
        </Table>
    );

    return (
        <Container fluid className="mt-4">
            <Row className="mb-4">
                <Col>
                    <h2 className="mb-4">Resultados de Votación Presupuesto Participativo</h2>
                    <Card className="shadow-sm">
                        <Card.Body>
                            <Row>
                                <Col md={4} className="text-center mb-3 mb-md-0">
                                    <h3>{data.estadisticas.total_votantes}</h3>
                                    <p className="text-muted">Total Votantes</p>
                                </Col>
                                <Col md={4} className="text-center mb-3 mb-md-0">
                                    <h3>{data.estadisticas.votos_online}</h3>
                                    <p className="text-muted">Votos Online</p>
                                </Col>
                                <Col md={4} className="text-center">
                                    <h3>{data.estadisticas.votos_presenciales}</h3>
                                    <p className="text-muted">Votos Presenciales</p>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row className="mb-4">
                <Col lg={8}>
                    <Card className="shadow-sm">
                        <Card.Header>
                            <h5 className="mb-0">Distribución de Votos por Categoría</h5>
                        </Card.Header>
                        <Card.Body>
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={dataBarras}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="name" />
                                    <YAxis />
                                    <Tooltip />
                                    <Legend />
                                    <Bar dataKey="votos" fill="#0088FE" />
                                </BarChart>
                            </ResponsiveContainer>
                        </Card.Body>
                    </Card>
                </Col>
                <Col lg={4}>
                    <Card className="shadow-sm">
                        <Card.Header>
                            <h5 className="mb-0">Tipo de Votación</h5>
                        </Card.Header>
                        <Card.Body>
                            <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                    <Pie
                                        data={dataPie}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={60}
                                        outerRadius={80}
                                        fill="#8884d8"
                                        dataKey="value"
                                        label
                                    >
                                        {dataPie.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row className="mb-4">
                <Col>
                    <Card className="shadow-sm">
                        <Card.Header className="bg-primary text-white">
                            <h5 className="mb-0">PP General - Trenque Lauquen</h5>
                        </Card.Header>
                        <Card.Body>
                            {renderResultTable(data.resultados['PP General']['Trenque Lauquen'], 'Trenque Lauquen')}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row className="mb-4">
                <Col>
                    <Card className="shadow-sm">
                        <Card.Header className="bg-success text-white">
                            <h5 className="mb-0">PP General - Beruti</h5>
                        </Card.Header>
                        <Card.Body>
                            {renderResultTable(data.resultados['PP General']['Beruti'], 'Beruti')}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row className="mb-4">
                <Col>
                    <Card className="shadow-sm">
                        <Card.Header className="bg-info text-white">
                            <h5 className="mb-0">PP General - 30 de Agosto</h5>
                        </Card.Header>
                        <Card.Body>
                            {renderResultTable(data.resultados['PP General']['30 de Agosto'], '30 de Agosto')}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row className="mb-4">
                <Col>
                    <Card className="shadow-sm">
                        <Card.Header className="bg-warning">
                            <h5 className="mb-0">PP Joven</h5>
                        </Card.Header>
                        <Card.Body>
                            {renderResultTable(data.resultados['PP Joven'], 'PP Joven')}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default ResultadosVotacion;